import React from 'react';
import ConfigurationService from "../../services/configuration-service";
import LicenseService from "../../services/license-service";
import {LicenseInfo} from "../../types/LicenseInfo";
import {WorkbenchState} from "../../types/WorkbenchState";
import BootstrapLoader from "./BootstrapLoader";
import WorkbenchStateContextProvider from "../context/WorkbenchContext";
import SecurityService from "../../services/security-service";
import {SecurityContext} from "../../types/SecurityContext";
import {Configurations} from "../../types/Configurations";

type WorkbenchBootstrapState = WorkbenchState & {
  hasLoaded: boolean;
  bootstrapErrors: Array<string>;
}

/**
 * Default workbench state when instantiating the context.
 */
const initialWorkbenchState: WorkbenchBootstrapState = {
  configurations: null,
  license: null,
  security: null,
  hasLoaded: false,
  bootstrapErrors: []
};

/**
 * Component that bootstraps the Workbench by fetching required server state and configures a state
 * context provider.
 */
class WorkbenchBootstrap extends React.Component<{}, WorkbenchBootstrapState> {

  errors = [
    "Can't load the Workbench!",
    "Some Platform service is inaccessible or request is not authenticated.",
    "Check console for errors and contact the Platform administrator."
  ];

  constructor(props: {}) {
    super(props);
    this.state = initialWorkbenchState;
    this.onUpdate = this.onUpdate.bind(this);
  }

  componentDidMount() {
    this.initialize();
  }

  private onUpdate() {
    this.initialize();
  }

  private initialize() {
    this.loadConfiguration()
      .then(() => this.initSecurity())
      .then(() => this.loadLicenseInfo())
      .then(() => this.setState({hasLoaded: true}))
      .catch((error: {reason: string, messages: Array<string>}) => {
        if (!this.shouldIgnoreErrors(error.reason)) {
          this.setState({
            bootstrapErrors: error.messages
          });
        }
      });
  }

  private loadConfiguration() {
    return ConfigurationService.getConfigurations()
      .then((configurations: Configurations) => {
        this.setState({configurations})
      })
      .catch((error) => this.handleError(error));
  }

  private initSecurity() {
    return SecurityService.initSecurity().then((securityContext: SecurityContext) => {
      // Update the global context
      this.setState({security: securityContext});
    }).catch((error) => this.handleError(error));
  }

  private loadLicenseInfo() {
    return LicenseService.getLicenseInfo()
      .then((license: LicenseInfo) => this.setLicense(license))
      .catch((license: LicenseInfo) => this.setLicense(license));
  }

  private setLicense(license: LicenseInfo) {
    this.setState({license});
  }

  private handleError(error: any) {
    console.error(error);
    return Promise.reject({
      reason: error?.reason,
      messages: this.errors
    });
  }

  private shouldIgnoreErrors(reason: string): boolean {
    return reason === SecurityService.REASONS.SHOULD_LOGIN;
  }

  render() {
    if (!this.state.hasLoaded) {
      return <BootstrapLoader bootstrapErrors={this.state.bootstrapErrors}/>;
    }
    return (
      <WorkbenchStateContextProvider state={this.state} onUpdate={this.onUpdate}>
        {this.props.children}
      </WorkbenchStateContextProvider>
    );
  }
}

export default WorkbenchBootstrap;
